import { ActivityCode } from '@app/AppConstants/ActivityCode';
import { PersonActivity, RosterCell } from '@app/Models';
import { PersonInfoModel } from '@app/Models/WebApiModels';
import { FunctionGroupIds, PersonFunctionsIds } from '@app/AppConstants/PersonFunctions';
import { AzureAuth, UserPermissions } from '@app/Components/AzureAuth';

export class ActivityService {
    private defaultHiddenActivityCode: Set<string> = new Set(['OBS2', 'GND1', 'GND2', 'CC10', 'CC11', 'CC12', 'CC13', 'ME1', 'ME2', 'TI1', 'TI2', 'FF1', 'FF2']);
    private defaultHiddenGrounActivityCode: Set<string> = new Set(['OBS2', 'CC10', 'CC11', 'CC12', 'CC13', 'ME1', 'ME2', 'TI1', 'TI2', 'FF1', 'FF2']);

    public buildActivityName(activityName: string, vacationType?: string | null): string {
        if (activityName === ActivityCode.VAC && vacationType) return vacationType;
        if (activityName === ActivityCode.VAC_ACC && vacationType) return vacationType + '/ACC';
        if (activityName === ActivityCode.VAC_ILL && vacationType) return vacationType + '/ILL';
        return activityName;
    }

    public getDefaultColumnVisibility(duties: PersonActivity[]) {
        const hasRosterGroundRead = AzureAuth.hasAccess(UserPermissions.RosterGroundRead);
        const defaultHidden = hasRosterGroundRead ? this.defaultHiddenGrounActivityCode : this.defaultHiddenActivityCode;

        const result: Set<string> = new Set([...defaultHidden]);

        duties.forEach((d) => {
            if (result.has(d.activityCodeName)) {
                result.delete(d.activityCodeName);
            }
        });

        return result;
    }

    public checkIsActivityAvailable(person: PersonInfoModel, activityCode: ActivityCode) {
        if (!person.function) return false;

        const { functionGroupId, planningFunctionGroupId } = person.function;
        const functionGroups: string[] = [];

        if (functionGroupId) {
            functionGroups.push(functionGroupId);
        }

        if (planningFunctionGroupId && planningFunctionGroupId !== functionGroupId) {
            functionGroups.push(planningFunctionGroupId);
        }

        for (const groupId of functionGroups) {
            if (groupId === FunctionGroupIds.Cabin && this._checkIsCabinActivityAvailable(activityCode)) {
                return true;
            }

            if (groupId === FunctionGroupIds.Cockpit && this._checkIsCockpitActivityAvailable(activityCode)) {
                return true;
            }

            if (groupId === FunctionGroupIds.Ground && this._checkIsGroundActivityAvailable(person, activityCode)) {
                return true;
            }
        }

        return false;
    }

    private _ownCellsSelected(selectedCells: RosterCell[], currentPersonId: string) {
        if (!selectedCells.length) {
            return false;
        }

        for (const cell of selectedCells) {
            if (cell.person.id !== currentPersonId)
                return false;
        }

        return true;
    }

    public isOwnChange(activityCode: ActivityCode, selectedCells: RosterCell[], currentPersonId: string) {
        const ownAllowedActivities: ActivityCode[] = [ActivityCode.PIKETT, ActivityCode.ADM, ActivityCode.INS, ActivityCode.TRV];
        return ownAllowedActivities.includes(activityCode) && this._ownCellsSelected(selectedCells, currentPersonId);
    }

    public isMinorChange(activityCodeName: ActivityCode, selectedCells: RosterCell[], currentPersonId: string) {
        if (selectedCells.length !== 1) {
            return false;
        }

        const hasPermission = AzureAuth.hasAccess(UserPermissions.RosterReleasedPlanMinorChanges);
        if (!hasPermission) {
            return false;
        }

        if (activityCodeName === ActivityCode.WISH && this._ownCellsSelected(selectedCells, currentPersonId)) {
            return true;
        }

        if (activityCodeName === ActivityCode.SWAP && activityCodeName !== ActivityCode.WISH) {
            return true;
        }

        return false;
    }

    public isLimitedChange(activityCodeName: ActivityCode, selectedCells: RosterCell[]) {
        if (selectedCells.length !== 1) {
            return false;
        }

        const hasPermission = AzureAuth.hasAccess(UserPermissions.RosterGroundLimitedWrite);
        if (!hasPermission) {
            return false;
        }

        const limitedActivities: ActivityCode[] = [ActivityCode.TRV, ActivityCode.ADM, ActivityCode.PIKETT, ActivityCode.SWAP];
        if (limitedActivities.includes(activityCodeName)) {
            return true;
        }

        return false;
    }

    private _checkIsCabinActivityAvailable(activityCode: ActivityCode) {
        return ActivityCode.allowedCabinActivities.includes(activityCode);
    }

    private _checkIsCockpitActivityAvailable(activityCode: ActivityCode) {
        return ActivityCode.allowedCockpitActivities.includes(activityCode);
    }

    private _checkIsGroundActivityAvailable(person: PersonInfoModel, activityCode: ActivityCode) {
        const functionId = person.function?.planningFunctionId;

        if (functionId === PersonFunctionsIds.OGE) {
            return ActivityCode.allowedOGEActivities.includes(activityCode);
        }

        if (functionId === PersonFunctionsIds.OGGE) {
            return ActivityCode.allowedOGGEActivities.includes(activityCode);
        }

        if (functionId === PersonFunctionsIds.OPCR) {
            return ActivityCode.allowedOPCRActivities.includes(activityCode);
        }

        if (functionId === PersonFunctionsIds.OMCC) {
            return ActivityCode.allowedOMCCActivities.includes(activityCode);
        }

        return false;
    }
}
